import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../supabase'
import Input from './shared/Input'
import CasavioLogo from './shared/CasavioLogo'
import i18n from '../i18n'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', danger: '#B03020',
}
const f = 'Georgia, serif'

const S = {
  outer: {
    minHeight: '100vh',
    background: C.bg,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '48px 24px 100px',
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  logo: {
    fontSize: '28px',
    fontWeight: '700',
    color: C.brown,
    letterSpacing: '4px',
    marginBottom: '6px',
    fontFamily: f,
  },
  tagline: {
    fontSize: '13px',
    color: C.muted,
    fontFamily: 'Calibri, sans-serif',
    marginBottom: '40px',
    textAlign: 'center',
  },
  card: {
    background: C.card,
    borderRadius: '20px',
    padding: '32px 28px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 24px rgba(44,29,20,0.08)',
    boxSizing: 'border-box',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: C.brown,
    marginBottom: '6px',
    fontFamily: f,
  },
  subtitle: {
    fontSize: '13px',
    color: C.muted,
    fontFamily: 'Calibri, sans-serif',
    marginBottom: '28px',
  },
  label: {
    fontSize: '11px',
    color: C.muted,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontFamily: 'Calibri, sans-serif',
    marginBottom: '6px',
    display: 'block',
  },
  input: {
    padding: '13px 14px',
    marginBottom: '16px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  btn: (disabled) => ({
    width: '100%',
    background: disabled
      ? C.border
      : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none',
    borderRadius: '10px',
    padding: '14px',
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: disabled ? C.muted : C.card,
    cursor: disabled ? 'default' : 'pointer',
    fontFamily: f,
    marginTop: '8px',
    transition: 'all 0.2s',
  }),
  link: {
    fontSize: '13px',
    color: C.amber,
    fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '20px',
    fontSize: '13px',
    color: C.muted,
    fontFamily: 'Calibri, sans-serif',
  },
  alert: (type) => ({
    background: type === 'error' ? '#FDECEA' : '#EBF5EF',
    border: `1px solid ${type === 'error' ? C.danger : '#2E6B4A'}44`,
    borderRadius: '10px',
    padding: '10px 14px',
    fontSize: '13px',
    color: type === 'error' ? C.danger : '#2E6B4A',
    fontFamily: 'Calibri, sans-serif',
    marginBottom: '16px',
  }),
  strength: (pct) => ({
    height: '3px',
    borderRadius: '2px',
    background: pct < 40 ? C.danger : pct < 70 ? C.amber : '#2E6B4A',
    width: `${pct}%`,
    transition: 'all 0.3s',
    marginBottom: '14px',
  }),
  tos: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginTop: '4px',
    marginBottom: '4px',
  },
  tosText: {
    fontSize: '12px',
    color: C.muted,
    fontFamily: 'Calibri, sans-serif',
    lineHeight: 1.5,
  },
}

function passwordStrength(pw) {
  let score = 0
  if (pw.length >= 8) score += 25
  if (pw.length >= 12) score += 15
  if (/[A-Z]/.test(pw)) score += 20
  if (/[0-9]/.test(pw)) score += 20
  if (/[^A-Za-z0-9]/.test(pw)) score += 20
  return Math.min(score, 100)
}

export default function Auth({ initialMode = 'signin' }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState(initialMode === 'signup' ? 'signup' : 'signin') // signin | signup | forgot
  const [refLabel, setRefLabel] = useState(null) // referrer label from /r/:code (ReferralLanding stashes casavio_ref_label)
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [error, setError] = useState(null)
  const [tosChecked, setTosChecked] = useState(false)
  const [ndaAccepted, setNdaAccepted] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [livePassword, setLivePassword] = useState('')
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem('casavio_preferred_language')
    if (stored) return stored
    return navigator.language?.startsWith('es') ? 'es' : 'en'
  })

  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '',
    email: '', password: '', confirmPassword: '',
  })

  // Tracks which sign-in fields the user has focused — used to gate blur validation
  const [siFocused, setSiFocused] = useState({ email: false, password: false })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (ref) localStorage.setItem('casavio_ref', ref)
    // Referrer label stashed by ReferralLanding (/r/:code) — drives the
    // "Invited by ..." header in signup mode.
    setRefLabel(localStorage.getItem('casavio_ref_label'))
  }, [])

  useEffect(() => {
    const stored = localStorage.getItem('casavio_preferred_language')
    if (stored) {
      if (stored !== i18n.language) i18n.changeLanguage(stored)
      setLang(stored)
    } else if (navigator.language?.startsWith('es') && i18n.language !== 'es') {
      i18n.changeLanguage('es')
      setLang('es')
    }
  }, [])

  const switchLanguage = (newLang) => {
    setLang(newLang)
    i18n.changeLanguage(newLang)
    localStorage.setItem('casavio_preferred_language', newLang)
  }

  const setField = (field) => (val) => setForm(f => ({ ...f, [field]: val }))

  const strength = passwordStrength(form.password)

  const autoCompletes = {
    email: 'email', password: 'current-password', confirmPassword: 'new-password',
    firstName: 'given-name', lastName: 'family-name', phone: 'tel',
  }
  const autoCapitalizes = {
    firstName: 'words', lastName: 'words',
    email: 'none', password: 'none', confirmPassword: 'none', phone: 'off',
  }

  const inp = (field, placeholder, type = 'text', onKeyDown, onBlur, onFocus) => (
    <Input
      type={type}
      placeholder={placeholder}
      value={form[field]}
      onChange={setField(field)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
      autoComplete={autoCompletes[field] || 'off'}
      autoCorrect="off"
      autoCapitalize={autoCapitalizes[field] || 'sentences'}
      spellCheck="false"
      style={S.input}
    />
  )

  const notifyFirstLogin = async (user) => {
    if (!user || user.user_metadata?.first_login_notified) return
    try {
      await supabase.auth.updateUser({ data: { first_login_notified: true } })
      const { data: profile } = await supabase
        .from('profiles').select('first_name, last_name').eq('id', user.id).maybeSingle()
      const name = profile
        ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
        : `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim()
      supabase.functions.invoke('notify-owner', {
        body: {
          event: 'first_login',
          data: { name, email: user.email, signed_in_at: new Date().toISOString() },
        },
      }).catch(() => {})
    } catch (e) {
      console.error('First login notify error:', e)
    }
  }

  // Mark a sign-in field as focused so blur validation can fire
  const handleSignInFocus = (field) => () => setSiFocused(t => ({ ...t, [field]: true }))

  // Show error on blur only if the field is empty and the user previously focused it
  const handleSignInBlur = (field) => (val) => {
    if (!val && siFocused[field]) {
      setAlert({ type: 'error', msg: t('auth.err_email_and_password') })
    }
  }

  // immediateVals: values read directly from e.target.value when Enter is pressed
  // (form state is stale until blur fires, so we read the DOM value instead)
  const handleSignIn = async (immediateVals = {}) => {
    const email = immediateVals.email ?? form.email
    const password = immediateVals.password ?? form.password
    if (!email || !password) {
      setSiFocused({ email: true, password: true })
      return setAlert({ type: 'error', msg: t('auth.err_email_and_password') })
    }
    setLoading(true)
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    setLoading(false)
    if (error) {
      setAlert({ type: 'error', msg: error.message })
    } else {
      if (data?.user) notifyFirstLogin(data.user)
      const pendingToken = localStorage.getItem('pending_invite_token')
      if (pendingToken) {
        localStorage.removeItem('pending_invite_token')
        window.location.href = `/invite/${pendingToken}`
      }
    }
  }

  const handleSignUp = async () => {
    if (!form.firstName || !form.lastName) return setError(t('auth.err_name'))
    if (!form.email) return setError(t('auth.err_email'))
    if (strength < 50) return setError(t('auth.err_weak_password'))
    if (form.password !== form.confirmPassword) return setError(t('auth.err_password_mismatch'))
    if (!tosChecked) return setError(t('auth.err_agree_tos'))
    if (!ndaAccepted) return setError(t('auth.err_agree_nda'))

    setLoading(true)
    setError(null)
    setAlert(null)

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: {
        data: {
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          full_name: `${form.firstName.trim()} ${form.lastName.trim()}`,
          phone: form.phone.trim(),
        },
      },
    })

    if (signUpError) {
      setError(signUpError.message)
      setLoading(false)
      return
    }

    const user = data?.user
    if (!user) {
      setError(t('auth.err_signup_failed'))
      setLoading(false)
      return
    }

    // Helper: run referral + invite redirect
    const finishSignup = () => {
      notifyFirstLogin(user)
      const ref = localStorage.getItem('casavio_ref')
      if (ref) {
        // Existing per-profile attribution (unchanged).
        supabase.from('profiles').update({ referred_by: ref }).eq('id', user.id).then(() => {})
        // Atomically bump referral_codes.signup_count server-side, then clear the
        // stash regardless of outcome. Non-blocking: a failed increment is an
        // audit gap, not a UX problem — the account is already created.
        supabase.functions.invoke('record-referral-scan', { body: { code: ref, event: 'signup' } })
          .then(({ error }) => { if (error) console.warn('[referral] signup_count increment failed:', error.message) })
          .catch((e) => console.warn('[referral] signup_count increment error:', e?.message || e))
          .finally(() => {
            localStorage.removeItem('casavio_ref')
            localStorage.removeItem('casavio_ref_label')
          })
      }
      const pendingToken = localStorage.getItem('pending_invite_token')
      if (pendingToken) {
        localStorage.removeItem('pending_invite_token')
        setAlert({ type: 'success', msg: t('auth.success_invite_redirect') })
        setTimeout(() => { window.location.href = `/invite/${pendingToken}` }, 1500)
      }
    }

    // No session means email confirmation is required — HomeSetup will handle home creation on first sign-in
    if (!data.session) {
      setLoading(false)
      finishSignup()
      setAlert({ type: 'success', msg: t('auth.success_check_email') })
      setTimeout(() => setMode('signin'), 3000)
      return
    }

    // Session active (email confirmation disabled) — create profile, home, and home_members now
    const { error: profileError } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        phone: form.phone?.trim() || null,
        nda_accepted: true,
        nda_accepted_at: new Date().toISOString(),
        language_preference: lang,
      }, { onConflict: 'id' })

    if (profileError) {
      console.error('Profile creation failed:', profileError)
    }

    const { data: home, error: homeError } = await supabase
      .from('homes')
      .insert({ created_by: user.id })
      .select()
      .single()

    if (homeError || !home) {
      console.error('Home creation failed:', homeError)
      setLoading(false)
      finishSignup()
      return
    }

    const { error: memberError } = await supabase
      .from('home_members')
      .insert({
        home_id: home.id,
        user_id: user.id,
        role: 'owner',
        experience_mode: 'standard',
      })

    if (memberError) {
      console.error('home_members failed:', memberError.message, memberError.details)
      setError('Account created but setup failed: ' + memberError.message)
      setLoading(false)
      return
    }

    setLoading(false)
    finishSignup()
    console.log('Signup complete for', user.email)
  }

  const handleForgot = async () => {
    if (!form.email) return setAlert({ type: 'error', msg: t('auth.err_email_address') })
    setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim())
    setLoading(false)
    if (error) {
      setAlert({ type: 'error', msg: error.message })
    } else {
      setAlert({ type: 'success', msg: t('auth.success_reset_sent') })
    }
  }

  return (
    <div style={{ ...S.outer, position: 'relative' }}>
      <div style={{ position: 'fixed', top: 16, right: 20, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Calibri, sans-serif', fontSize: 13, zIndex: 10 }}>
        <button
          onClick={() => switchLanguage('en')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', color: lang === 'en' ? C.amber : C.muted, fontWeight: lang === 'en' ? 700 : 400 }}
        >
          {t('language_toggle.english')}
        </button>
        <span style={{ color: C.muted }}>|</span>
        <button
          onClick={() => switchLanguage('es')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', color: lang === 'es' ? C.amber : C.muted, fontWeight: lang === 'es' ? 700 : 400 }}
        >
          {t('language_toggle.spanish')}
        </button>
      </div>
      <CasavioLogo size={26} color={C.brown} style={{ marginBottom: '6px' }} />
      <div style={S.tagline}>{t('auth.tagline')}</div>

      <div style={S.card}>
        {mode === 'signin' && (
          <>
            <div style={S.title}>{t('auth.signin_title')}</div>
            <div style={S.subtitle}>{t('auth.signin_subtitle')}</div>
            {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}
            <label style={S.label}>{t('auth.email_label')}</label>
            {inp('email', t('auth.email_placeholder'), 'email',
              (e) => e.key === 'Enter' && handleSignIn({ email: e.target.value }),
              handleSignInBlur('email'),
              handleSignInFocus('email')
            )}
            <label style={S.label}>{t('auth.password_label')}</label>
            {inp('password', t('auth.password_placeholder'), 'password',
              (e) => e.key === 'Enter' && handleSignIn({ password: e.target.value }),
              handleSignInBlur('password'),
              handleSignInFocus('password')
            )}
            <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '16px' }}>
              <button style={S.link} onClick={() => { setMode('forgot'); setAlert(null); setError(null); setSiFocused({ email: false, password: false }) }}>{t('auth.forgot_password_link')}</button>
            </div>
            <button style={S.btn(loading)} onClick={handleSignIn}>
              {loading ? t('auth.signing_in') : t('auth.sign_in_btn')}
            </button>
            <div style={S.switchRow}>
              <span>{t('auth.no_account')}</span>
              <button style={S.link} onClick={() => { setMode('signup'); setAlert(null); setError(null); setSiFocused({ email: false, password: false }) }}>{t('auth.sign_up_link')}</button>
            </div>
          </>
        )}

        {mode === 'signup' && (
          <>
            {refLabel && (
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 19, fontWeight: 700, color: '#B8721F', textAlign: 'center', marginBottom: 14 }}>
                Invited by {refLabel}
              </div>
            )}
            <div style={S.title}>{t('auth.signup_title')}</div>
            <div style={S.subtitle}>{t('auth.signup_subtitle')}</div>
            {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}
            {error && (
              <div style={{
                background: '#FFF0F0', border: '1px solid #DC2626',
                borderRadius: 8, padding: 12, marginBottom: 16,
                fontSize: 14, color: '#DC2626', lineHeight: 1.5,
              }}>
                {error}
                {error.toLowerCase().includes('already registered') && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      onClick={() => { setMode('signin'); setAlert(null); setError(null) }}
                      style={{ background: 'none', border: 'none', color: '#B8721F', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
                    >
                      {t('auth.sign_in_instead')}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div style={S.row}>
              <div>
                <label style={S.label}>{t('auth.first_name_label')}</label>
                {inp('firstName', t('auth.first_name_placeholder'))}
              </div>
              <div>
                <label style={S.label}>{t('auth.last_name_label')}</label>
                {inp('lastName', t('auth.last_name_placeholder'))}
              </div>
            </div>
            <label style={S.label}>{t('auth.phone_label')}</label>
            {inp('phone', t('auth.phone_placeholder'), 'tel')}
            <label style={S.label}>{t('auth.email_label')}</label>
            {inp('email', t('auth.email_placeholder'), 'email')}
            <label style={S.label}>{t('auth.preferredLanguage')}</label>
            <select
              value={lang}
              onChange={e => switchLanguage(e.target.value)}
              style={{
                width: '100%', background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: '10px', padding: '13px 14px', fontSize: '16px',
                fontFamily: f, color: C.brown, boxSizing: 'border-box',
                outline: 'none', marginBottom: '16px', cursor: 'pointer',
                appearance: 'none', WebkitAppearance: 'none',
              }}
            >
              <option value="en">{t('auth.english')}</option>
              <option value="es">{t('auth.spanish')}</option>
            </select>
            <label style={S.label}>{t('auth.password_label')}</label>
            <Input
              type="password"
              placeholder={t('auth.password_create_placeholder')}
              value={form.password}
              onChange={setField('password')}
              onFocus={() => setPasswordFocused(true)}
              onBlur={(val) => { setPasswordFocused(false); setLivePassword(val) }}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck="false"
              style={S.input}
              onInput={(e) => setLivePassword(e.target.value)}
            />
            {(passwordFocused || livePassword.length > 0) && (
              <div style={{ marginBottom: '14px', marginTop: '-8px' }}>
                {[
                  { key: 'auth.passwordReqLength', met: livePassword.length >= 8 },
                  { key: 'auth.passwordReqNumber', met: /\d/.test(livePassword) },
                  { key: 'auth.passwordReqLetter', met: /[a-zA-Z]/.test(livePassword) },
                ].map(({ key, met }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', fontSize: '12px', fontFamily: 'Calibri, sans-serif', color: met ? '#2E6B4A' : C.muted, transition: 'color 0.2s' }}>
                    <span style={{ fontWeight: 'bold', flexShrink: 0 }}>{met ? '✓' : '○'}</span>
                    {t(key)}
                  </div>
                ))}
              </div>
            )}
            {form.password.length > 0 && (
              <>
                <div style={{ ...S.strength(strength), marginBottom: '4px' }} />
                <div style={{ fontSize: '11px', color: strength < 40 ? C.danger : strength < 70 ? C.amber : '#2E6B4A', fontFamily: 'Calibri, sans-serif', marginBottom: '14px' }}>
                  {strength < 40 ? t('auth.password_weak') : strength < 70 ? t('auth.password_fair') : t('auth.password_strong')}
                </div>
              </>
            )}
            <label style={S.label}>{t('auth.confirm_password_label')}</label>
            {inp('confirmPassword', t('auth.confirm_password_placeholder'), 'password')}
            <div style={{ background: '#FBF3E8', border: '1px solid #B8721F44', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', fontWeight: '600', letterSpacing: '0.3px', marginBottom: '12px' }}>
              {t('auth.bothAgreementsRequired')}
            </div>
            <label style={{ ...S.tos, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={tosChecked}
                onChange={e => setTosChecked(e.target.checked)}
                style={{ marginTop: '2px', accentColor: C.amber, flexShrink: 0 }}
              />
              <span style={S.tosText}>
                {t('auth.tos_agree')}{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.amber, textDecoration: 'underline' }}>{t('auth.tos_link')}</a>
                {' '}{t('auth.tos_and')}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.amber, textDecoration: 'underline' }}>{t('auth.privacy_link')}</a>
              </span>
            </label>
            <label style={{ ...S.tos, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ndaAccepted}
                onChange={e => setNdaAccepted(e.target.checked)}
                style={{ marginTop: '2px', accentColor: C.amber, flexShrink: 0 }}
              />
              <span style={S.tosText}>
                {t('auth.nda_agree')}{' '}
                <a href="/nda" target="_blank" rel="noopener noreferrer" style={{ color: C.amber, textDecoration: 'underline' }}>{t('auth.nda_link')}</a>
              </span>
            </label>
            <button style={S.btn(loading)} onClick={handleSignUp}>
              {loading ? t('auth.creating_account') : t('auth.create_account_btn')}
            </button>
            <div style={S.switchRow}>
              <span>{t('auth.already_have_account')}</span>
              <button style={S.link} onClick={() => { setMode('signin'); setAlert(null); setError(null); setSiFocused({ email: false, password: false }) }}>{t('auth.sign_in_link')}</button>
            </div>
          </>
        )}

        {mode === 'forgot' && (
          <>
            <div style={S.title}>{t('auth.forgot_title')}</div>
            <div style={S.subtitle}>{t('auth.forgot_subtitle')}</div>
            {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}
            <label style={S.label}>{t('auth.email_label')}</label>
            {inp('email', t('auth.email_placeholder'), 'email')}
            <button style={S.btn(loading)} onClick={handleForgot}>
              {loading ? t('auth.sending') : t('auth.send_reset_link')}
            </button>
            <div style={S.switchRow}>
              <button style={S.link} onClick={() => { setMode('signin'); setAlert(null); setSiFocused({ email: false, password: false }) }}>{t('auth.back_to_sign_in')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}